import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useOnlyOfficeEditFileSettings } from "@/modules/document-register/features/edit-file-with-only-office/model/use-only-office-edit-file-settings";
import { OnlyOfficeEditor } from "@/shared/only-office";
import { useTranslations } from "use-intl";
import { IConfig } from "@onlyoffice/document-editor-react";
import { useUser } from "@/application/providers/user";
import { useFileKeyContext } from "@/shared/only-office/utils/use-file-key-context";

export type EditFileWithOnlyOfficeHandle = {
  key: string | undefined;
};

export const EditFileWithOnlyOffice = forwardRef<
  EditFileWithOnlyOfficeHandle,
  {
    fileId: string;
    plugins?: NonNullable<IConfig["editorConfig"]>["plugins"];
    canEdit?: boolean;
    refetchFactor?: unknown;
    type?: string;
  }
>(({ fileId, plugins, canEdit = true, refetchFactor, type }, ref) => {
  const t = useTranslations("DocumentRegister");
  const { settings, extension, filename, refetch } =
    useOnlyOfficeEditFileSettings({
      fileId,
    });
  const user = useUser();

  useImperativeHandle(ref, () => ({
    key: settings?.key,
  }));

  const fileKeyContext = useFileKeyContext();

  useEffect(() => {
    if (settings?.key && fileKeyContext?.setFileKey) {
      fileKeyContext.setFileKey(settings.key);
    }
  }, [fileKeyContext, settings?.key]);

  useEffect(() => {
    if (refetchFactor !== undefined) {
      refetch();
    }
  }, [refetchFactor, refetch]);

  if (!settings) return null;

  return (
    <OnlyOfficeEditor
      key={settings.key}
      height={"100%"}
      documentServerUrl={settings.onlyOfficeUrl}
      config={{
        type,
        document: {
          fileType: extension ?? "docx",
          key: settings.key,
          title: canEdit
            ? t("Edit document")
            : filename
              ? filename
              : t("Document"),
          url: settings.editFileUrl,
          permissions: {
            edit: canEdit,
          },
        },
        editorConfig: {
          plugins,
          callbackUrl: settings.saveCallbackUrl,
          customization: {
            forcesave: true,
            autosave: true,
            pointerMode: "select",
            slidePlayerBackground: "",
            zoom: -2,
          },
          user: {
            name: user?.person?.fullName,
          },
        },
      }}
    />
  );
});

EditFileWithOnlyOffice.displayName = "EditFileWithOnlyOffice";
